import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Building, Camera, Upload, Save, Eye, Star, MapPin, Phone, Mail, Globe,
  Briefcase, Award, CheckCircle, Clock, Users, TrendingUp, X, Image,
  AlertCircle, Info
} from 'lucide-react';

// Specializations with labels
const SPECIALIZATIONS = [
  { value: 'residenziale', label: 'Residenziale' },
  { value: 'lusso', label: 'Lusso' },
  { value: 'investimenti', label: 'Investimenti' },
  { value: 'nuova_costruzione', label: 'Nuova Costruzione' },
  { value: 'commerciale', label: 'Commerciale' },
  { value: 'affitti', label: 'Affitti' },
  { value: 'ville_e_casali', label: 'Ville e Casali' }
];

// WEQASA Score Bar Component
const WeqasaScoreBar = ({ level, totalScore }) => {
  return (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Valutazione WEQASA</span>
        <span className="text-sm text-gray-500">{Math.round(totalScore)}/100</span>
      </div>
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((bar) => (
          <div
            key={bar}
            className={`h-3 flex-1 rounded-full transition-all ${
              bar <= level 
                ? 'bg-gradient-to-r from-[#0071e3] to-[#005BB5]' 
                : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Agency Profile Card Preview Component
export const AgencyProfileCard = ({ profile, agent, weqasaScore, performance, isPreview = false }) => {
  if (!agent) return null;
  
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('http')) return url;
    if (url.startsWith('simulated://')) return null;
    return url;
  };

  const coverUrl = getImageUrl(profile?.cover_image_url);
  const logoUrl = getImageUrl(profile?.logo_url);
  const badges = weqasaScore?.badges || [];
  
  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden ${isPreview ? 'border-2 border-dashed border-[#0071e3]' : ''}`} data-testid="agency-profile-card">
      {/* Cover Image */}
      <div className="relative h-40 md:h-48 bg-gradient-to-r from-[#0071e3] to-[#1a6b7c]">
        {coverUrl ? (
          <img 
            src={coverUrl} 
            alt="Cover"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building className="h-16 w-16 text-white/30" />
          </div>
        )}
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        
        {/* Logo Overlay */}
        <div className="absolute -bottom-12 left-6">
          <div className="w-24 h-24 rounded-xl bg-white shadow-lg border-4 border-white overflow-hidden">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Logo"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#0071e3] flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {(profile?.company_name || agent?.company_name || 'A')[0]}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Preview badge */}
        {isPreview && (
          <div className="absolute top-4 right-4 bg-[#0071e3] text-white text-xs px-3 py-1 rounded-full">
            Anteprima
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="pt-16 px-6 pb-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {profile?.company_name || agent?.company_name || 'Nome Agenzia'}
            </h3>
            <p className="text-sm text-gray-500 flex items-center mt-1">
              <MapPin className="h-4 w-4 mr-1" />
              {profile?.city || agent?.registered_address?.split('-')[1]?.trim() || 'Località non specificata'}
              {profile?.province && ` (${profile.province})`}
            </p>
          </div>
          
          {/* WEQASA Score */}
          {weqasaScore && (
            <div className="text-right">
              <WeqasaScoreBar level={weqasaScore.level} totalScore={weqasaScore.total_score} />
            </div>
          )}
        </div>
        
        {/* Badges */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {badges.map((badge, idx) => (
              <span
                key={idx}
                className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium ${
                  badge.color === 'green' ? 'bg-green-100 text-green-700' :
                  badge.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                  badge.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                  badge.color === 'gold' ? 'bg-[#0071e3]/20 text-[#0071e3]' :
                  badge.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-700'
                }`}
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                {badge.label}
              </span>
            ))}
          </div>
        )}
        
        {/* Short Description */}
        {profile?.description_short && (
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">
            {profile.description_short}
          </p>
        )}
        
        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-4 mb-4 py-4 border-y border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#0071e3]">
              {profile?.years_experience || '-'}
            </p>
            <p className="text-xs text-gray-500">Anni Esperienza</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-[#0071e3]">
              {profile?.properties_sold || performance?.mandates_won || '-'}
            </p>
            <p className="text-xs text-gray-500">Immobili Venduti</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {performance?.acceptance_rate ? `${performance.acceptance_rate}%` : '-'}
            </p>
            <p className="text-xs text-gray-500">Tasso Successo</p>
          </div>
        </div>
        
        {/* Specializations */}
        {profile?.specializations && profile.specializations.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2">Specializzazioni</p>
            <div className="flex flex-wrap gap-2">
              {profile.specializations.map((spec, idx) => (
                <span key={idx} className="bg-[#0071e3]/10 text-[#0071e3] text-xs px-2 py-1 rounded-full">
                  {SPECIALIZATIONS.find(s => s.value === spec)?.label || spec}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Contact Info */}
        <div className="flex flex-wrap gap-4 text-sm">
          {(profile?.phone || agent?.phone) && (
            <a href={`tel:${profile?.phone || agent?.phone}`} className="flex items-center text-gray-600 hover:text-[#0071e3]">
              <Phone className="h-4 w-4 mr-1" />
              {profile?.phone || agent?.phone}
            </a>
          )}
          {(profile?.email || agent?.certified_email) && (
            <a href={`mailto:${profile?.email || agent?.certified_email}`} className="flex items-center text-gray-600 hover:text-[#0071e3]">
              <Mail className="h-4 w-4 mr-1" />
              {profile?.email || agent?.certified_email}
            </a>
          )}
          {profile?.website && (
            <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-600 hover:text-[#0071e3]">
              <Globe className="h-4 w-4 mr-1" />
              Sito Web
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Agency Profile Editor Component
const AgencyProfileEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({});
  const [agent, setAgent] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [weqasaScore, setWeqasaScore] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/agent/profile');
      setProfile(response.data.profile || {});
      setAgent(response.data.agent);
      setCompleteness(response.data.completeness);
      setWeqasaScore(response.data.weqasa_score);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Errore nel caricamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecializationToggle = (spec) => {
    const current = profile.specializations || [];
    if (current.includes(spec)) {
      handleChange('specializations', current.filter(s => s !== spec));
    } else {
      handleChange('specializations', [...current, spec]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put('/agent/profile', profile);
      setCompleteness(response.data.completeness);
      setWeqasaScore(response.data.weqasa_score);
      toast.success('Profilo salvato con successo!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Formato non valido. Usa JPG o PNG');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Max 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = type === 'logo' ? '/agent/profile/logo' : '/agent/profile/cover';
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update local state with new URL
      handleChange(type === 'logo' ? 'logo_url' : 'cover_image_url', response.data.url);
      toast.success(`${type === 'logo' ? 'Logo' : 'Copertina'} caricata!`);
      
      // Refresh to get updated scores
      fetchProfile();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0071e3]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Profilo Agenzia</h2>
          <p className="text-gray-500">Personalizza il profilo della tua agenzia per i venditori</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? 'Nascondi' : 'Anteprima'}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0071e3] hover:bg-[#005BB5]">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Profilo'}
          </Button>
        </div>
      </div>

      {/* Completeness Warning */}
      {completeness && completeness.percentage < 80 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Completa il tuo profilo per migliorare il ranking</p>
            <p className="text-sm text-amber-700 mt-1">
              Profilo completato al {completeness.percentage}%. 
              Campi mancanti: {completeness.missing_fields.slice(0, 3).join(', ')}
              {completeness.missing_fields.length > 3 && ` e altri ${completeness.missing_fields.length - 3}`}
            </p>
          </div>
        </div>
      )}

      <div className={`grid ${showPreview ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-8`}>
        {/* Form Section */}
        <div className="space-y-6">
          {/* Images Section */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Camera className="h-5 w-5 mr-2 text-[#0071e3]" />
              Immagini
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <div>
                <Label className="mb-2 block">Logo Agenzia (1:1)</Label>
                <div 
                  className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#0071e3] transition-colors bg-gray-50"
                  onClick={() => logoInputRef.current?.click()}
                >
                  {profile.logo_url ? (
                    <div className="relative w-full h-full">
                      <img 
                        src={profile.logo_url} 
                        alt="Logo" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                        <span className="text-white text-sm">Cambia</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Min 400x400 px</span>
                    </>
                  )}
                </div>
                <input 
                  ref={logoInputRef}
                  type="file" 
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => handleImageUpload('logo', e.target.files?.[0])}
                />
              </div>

              {/* Cover Upload */}
              <div>
                <Label className="mb-2 block">Immagine di Copertina (3:1)</Label>
                <div 
                  className="aspect-[3/1] border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#0071e3] transition-colors bg-gray-50"
                  onClick={() => coverInputRef.current?.click()}
                >
                  {profile.cover_image_url ? (
                    <div className="relative w-full h-full">
                      <img 
                        src={profile.cover_image_url} 
                        alt="Cover" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                        <span className="text-white text-sm">Cambia</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Image className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Min 1200x400 px</span>
                    </>
                  )}
                </div>
                <input 
                  ref={coverInputRef}
                  type="file" 
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => handleImageUpload('cover', e.target.files?.[0])}
                />
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="h-5 w-5 mr-2 text-[#0071e3]" />
              Informazioni Azienda
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nome Azienda *</Label>
                <Input 
                  value={profile.company_name || agent?.company_name || ''} 
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="Es. Immobiliare Milano"
                />
              </div>
              <div>
                <Label>Indirizzo</Label>
                <Input 
                  value={profile.address || ''} 
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Via Roma 1"
                />
              </div>
              <div>
                <Label>Città *</Label>
                <Input 
                  value={profile.city || ''} 
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Milano"
                />
              </div>
              <div>
                <Label>Provincia *</Label>
                <Input 
                  value={profile.province || ''} 
                  onChange={(e) => handleChange('province', e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="MI"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          {/* Descriptions */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Info className="h-5 w-5 mr-2 text-[#0071e3]" />
              Descrizioni
            </h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Descrizione Breve *</Label>
                  <span className="text-xs text-gray-400">{(profile.description_short || '').length}/500</span>
                </div>
                <Textarea 
                  value={profile.description_short || ''} 
                  onChange={(e) => handleChange('description_short', e.target.value.slice(0, 500))}
                  placeholder="Presenta brevemente la tua agenzia..."
                  rows={3}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Descrizione Estesa (opzionale)</Label>
                  <span className="text-xs text-gray-400">{(profile.description_long || '').length}/1500</span>
                </div>
                <Textarea 
                  value={profile.description_long || ''} 
                  onChange={(e) => handleChange('description_long', e.target.value.slice(0, 1500))}
                  placeholder="Descrivi in dettaglio i servizi e i punti di forza..."
                  rows={5}
                />
              </div>
            </div>
          </div>

          {/* Experience & Stats */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Award className="h-5 w-5 mr-2 text-[#0071e3]" />
              Esperienza e Statistiche
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Anni di Esperienza *</Label>
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  value={profile.years_experience || ''} 
                  onChange={(e) => handleChange('years_experience', parseInt(e.target.value) || null)}
                  placeholder="Es. 15"
                />
              </div>
              <div>
                <Label>Immobili Venduti (opzionale)</Label>
                <Input 
                  type="number"
                  min="0"
                  value={profile.properties_sold || ''} 
                  onChange={(e) => handleChange('properties_sold', parseInt(e.target.value) || null)}
                  placeholder="Es. 250"
                />
              </div>
            </div>
          </div>

          {/* Specializations */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Briefcase className="h-5 w-5 mr-2 text-[#0071e3]" />
              Specializzazioni *
            </h3>
            
            <div className="flex flex-wrap gap-2">
              {SPECIALIZATIONS.map((spec) => {
                const isSelected = (profile.specializations || []).includes(spec.value);
                return (
                  <button
                    key={spec.value}
                    type="button"
                    onClick={() => handleSpecializationToggle(spec.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      isSelected 
                        ? 'bg-[#0071e3] text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {isSelected && <CheckCircle className="h-4 w-4 inline mr-1" />}
                    {spec.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Phone className="h-5 w-5 mr-2 text-[#0071e3]" />
              Contatti
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Telefono *</Label>
                <Input 
                  value={profile.phone || agent?.phone || ''} 
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+39 02 1234567"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={profile.email || agent?.certified_email || ''} 
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="info@agenzia.it"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Sito Web (opzionale)</Label>
                <Input 
                  type="url"
                  value={profile.website || ''} 
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://www.agenzia.it"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        {showPreview && (
          <div className="lg:sticky lg:top-6 h-fit">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">Anteprima Profilo</h3>
              <Badge className="bg-[#0071e3]/20 text-[#0071e3]">Live Preview</Badge>
            </div>
            <AgencyProfileCard 
              profile={profile}
              agent={agent}
              weqasaScore={weqasaScore}
              performance={{ mandates_won: 0, acceptance_rate: 0 }}
              isPreview={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AgencyProfileEditor;
